/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  EXECUTION_TYPE,
  UiController,
  breakdownList,
  commands,
  consoleCommands,
  events,
} from 'modules/common/ui-controller';
import { expandFocusOnAll, getValuesFromList, isIncluded } from 'modules/common/utils/array';
import {
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  createVisualCollapseStep,
} from 'modules/common/ui-controller/utils/shared-command-steps';
import { regionalMapActions } from '../slice';

const isLoaded = (context) => context.isLoaded;
// focus on command with the EMPTY value clears the focus on selection
const isEmptyFocusOn = (value) => value?.length === 1 && value[0] === EXECUTION_TYPE.EMPTY;
// time given to the tabular view to load before the commands that follow the tab filters are applied
const TABULAR_VIEW_DELAY = 4000;
/**
 * Regional map tab filter commands in the order they are applied.
 * The position in this list is the execution order, see useCommandPipeline.
 * context is built in useRegionalMapCommandPipeline.
 */
const REGIONAL_MAP_FILTER_COMMAND_STEPS = [
  {
    command: consoleCommands.BOOKING_DATE,
    event: events.SET_RELATION_MAP_CUSTOM_BOOKING,
    // open custom booking date picker, the date range is applied once it is opened
    prepare: (value, { dispatch }) => {
      if (value !== commands.OTB) {
        dispatch(regionalMapActions.setRegionalMapOtb(true));
      }
    },
    ready: (context, { pending }) =>
      context.isLoaded &&
      (pending[consoleCommands.BOOKING_DATE] === commands.OTB || Boolean(context.customBooking)),
    apply: (value, { dispatch, resetBookingDate }) => {
      // checks command value is 'OTB'
      if (value === commands.OTB) {
        dispatch(regionalMapActions.setRegionalMapOtb(false));
        dispatch(regionalMapActions.setRegionMapBookingDate(resetBookingDate));
      } else {
        UiController.setBookingDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.SEGMENT,
    event: events.SET_REGINAL_MAP_BREAKDOWN,
    ready: (context) => context.isLoaded && context.chartBreakdownList?.length > 0,
    apply: (value, { dispatch, chartBreakdownList, isDetailDisabled }) => {
      // get breakdown option
      const breakdownOption = breakdownList[value?.substring(0, 2)];
      // get isDetailed state from command value
      const detailsStatus = parseInt(value?.substring(2, 4), 10);
      if (breakdownOption !== undefined) {
        // get selected breakdown option from command value
        const option = chartBreakdownList?.filter((val) => val?.label?.includes(breakdownOption));
        if (option?.length > 0) {
          dispatch(regionalMapActions.setRegionMapChartBreakdown(option[0]?.id));
          dispatch(regionalMapActions.setRegionMapFocusOn([]));
        }
        if (!Number.isNaN(detailsStatus) && !isDetailDisabled) {
          dispatch(regionalMapActions.setIsRegionDetailed(detailsStatus === 1));
        }
      }
    },
  },
  {
    command: consoleCommands.FOCUS,
    event: events.SET_REGIONAL_MAP_FOCUS_ON,
    // wait until the focus on list belongs to the selected breakdown and contains the command values
    ready: (context, { pending }) => {
      const value = pending[consoleCommands.FOCUS];
      if (!context.isLoaded || !context.isFocusOnListReady || !(context.focusOnList?.length > 0)) {
        return false;
      }
      if (isEmptyFocusOn(value)) return true;
      // replace ALL command value with every focus on option
      const focusOnValues = expandFocusOnAll(value, context.focusOnList);
      return Boolean(focusOnValues) && isIncluded(context.focusOnList, focusOnValues);
    },
    apply: (value, { dispatch, focusOnList }) => {
      if (isEmptyFocusOn(value)) {
        dispatch(regionalMapActions.setRegionMapFocusOn([]));
        return;
      }
      // get selected focus on values from command value
      const optionsSelected = getValuesFromList(expandFocusOnAll(value, focusOnList), focusOnList);
      dispatch(
        regionalMapActions.setRegionMapFocusOn(optionsSelected?.length > 0 ? optionsSelected : [])
      );
    },
  },
  {
    command: consoleCommands.TABULAR_VIEW,
    event: events.SET_REGIONAL_MAP_TABULAR_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(regionalMapActions.setRegionalBreakdownTabularView(Number(value) === 1));
    },
    delayAfter: TABULAR_VIEW_DELAY,
  },
];

// time the regional map visuals stay hidden before the visual collapse command value is applied
const VISUAL_COLLAPSE_DELAY = 2000;
// actions to show or hide the regional map visuals
const VISUAL_DISPLAY_ACTIONS = {
  RV: regionalMapActions.setRegionMapIsDisplay,
  RTV: regionalMapActions.setRegionMapTabularIsDisplay,
};
/**
 * Regional map tab commands of a command url in the order they are applied
 */
const REGIONAL_MAP_COMMAND_STEPS = [
  ...REGIONAL_MAP_FILTER_COMMAND_STEPS,
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  createVisualCollapseStep(
    events.SET_REGIONAL_MAP_VISUAL_COLLAPSE,
    VISUAL_COLLAPSE_DELAY,
    () => VISUAL_DISPLAY_ACTIONS
  ),
];

export default REGIONAL_MAP_COMMAND_STEPS;
